/* eslint-disable react/no-unescaped-entities */
import React from 'react';
import LegalPageLayout from './LegalPageLayout';
import { useConsent } from '../../context/ConsentContext';

const CookieRow = ({ name, provider, purpose, category, duration, page }) => (
  <tr className="border-b border-white/10">
    <td className="py-2 pr-3 align-top"><code className="text-xs text-pink-300">{name}</code></td>
    <td className="py-2 pr-3 align-top text-sm">{provider}</td>
    <td className="py-2 pr-3 align-top text-sm">{purpose}</td>
    <td className="py-2 pr-3 align-top"><span className="inline-block px-2 py-0.5 rounded-full text-[10px] font-bold border border-white/10">{category}</span></td>
    <td className="py-2 pr-3 align-top text-xs text-zinc-400">{duration}</td>
    <td className="py-2 align-top text-xs text-zinc-400">{page}</td>
  </tr>
);

const CookiePolicyFR = () => {
  const { openPreferences } = useConsent();
  return (
    <LegalPageLayout
      title="Politique cookies"
      subtitle="Inventaire et finalité des cookies, traceurs et stockages utilisés sur body-up.ch."
      lastUpdated="Dernière mise à jour : 21 juin 2026"
      seoTitle="Politique cookies | Body-Up Personal Training Nyon"
      seoPath="/politique-cookies"
    >
      <h2>1. Qu'est-ce qu'un cookie ?</h2>
      <p>Un cookie est un petit fichier déposé par votre navigateur lorsque vous visitez un site. Le terme cookie inclut ici les technologies équivalentes : <code>localStorage</code>, <code>sessionStorage</code> et pixels.</p>

      <h2>2. Catégories</h2>
      <ul>
        <li><strong>Nécessaire</strong> : indispensable au fonctionnement du site (authentification, langue, choix de consentement). Toujours actif.</li>
        <li><strong>Analytique</strong> : mesure d'audience. <strong>Désactivé par défaut.</strong> Aucun outil analytique n'est actif à ce jour ; votre choix ici ne sera utilisé que lorsque Body-Up activera un tel outil.</li>
        <li><strong>Marketing</strong> : publicité et conversion. <strong>Désactivé par défaut.</strong> Idem, aucun outil marketing n'est actif aujourd'hui.</li>
      </ul>

      <h2>3. Inventaire actuel</h2>
      <p>Cet inventaire est volontairement transparent : il liste uniquement ce qui est <em>réellement</em> chargé aujourd'hui sur le site, sans inventer de fournisseur fictif.</p>

      <h3>3.1 Cookies / localStorage strictement nécessaires</h3>
      <div className="not-prose overflow-x-auto">
        <table className="w-full text-left">
          <thead className="text-xs uppercase text-zinc-500 border-b border-white/10">
            <tr>
              <th className="py-2 pr-3">Nom</th>
              <th className="py-2 pr-3">Fournisseur</th>
              <th className="py-2 pr-3">Finalité</th>
              <th className="py-2 pr-3">Catégorie</th>
              <th className="py-2 pr-3">Durée</th>
              <th className="py-2">Page(s)</th>
            </tr>
          </thead>
          <tbody>
            <CookieRow name="bodyup_consent_v1" provider="Body-Up" purpose="Stocke votre choix de consentement (catégories, version, date)." category="Nécessaire" duration="12 mois" page="Toutes" />
            <CookieRow name="bodyup_client_token" provider="Body-Up" purpose="Jeton de session JWT de l'espace client après connexion." category="Nécessaire" duration="Session / 7 jours" page="/espace-client, /mon-espace" />
            <CookieRow name="bodyup-language" provider="Body-Up" purpose="Mémorise votre langue (FR/EN)." category="Nécessaire" duration="12 mois" page="Toutes" />
            <CookieRow name="bodyup_guide_* / bodyup_tour_*" provider="Body-Up" purpose="Mémorise les guides et tours interactifs déjà vus." category="Nécessaire" duration="12 mois" page="/mon-espace" />
            <CookieRow name="bodyup_tooltip_*" provider="Body-Up" purpose="Mémorise les info-bulles déjà vues pour ne pas les répéter." category="Nécessaire" duration="12 mois" page="/mon-espace" />
          </tbody>
        </table>
      </div>

      <h3>3.2 Cookies tiers chargés sans consentement (services techniques)</h3>
      <div className="not-prose overflow-x-auto">
        <table className="w-full text-left">
          <thead className="text-xs uppercase text-zinc-500 border-b border-white/10">
            <tr>
              <th className="py-2 pr-3">Nom</th>
              <th className="py-2 pr-3">Fournisseur</th>
              <th className="py-2 pr-3">Finalité</th>
              <th className="py-2 pr-3">Catégorie</th>
              <th className="py-2 pr-3">Durée</th>
              <th className="py-2">Page(s)</th>
            </tr>
          </thead>
          <tbody>
            <CookieRow name="Stripe (__stripe_*)" provider="Stripe" purpose="Sécurité du checkout paiement. Chargés uniquement sur la page de paiement." category="Nécessaire" duration="≤ 1 an" page="Paiement / Stripe Checkout" />
          </tbody>
        </table>
      </div>

      <p className="mb-3 text-zinc-400 text-sm">
        <strong>Note :</strong> Les polices d'écriture sont maintenant <strong>hébergées localement</strong> sur nos serveurs (plus de connexion à Google Fonts).
      </p>

      <h3>3.3 Cookies analytiques et marketing</h3>
      <p>
        <strong>Aucun outil analytique ou marketing n'est actuellement chargé sur body-up.ch.</strong> Google Analytics (GA4) et Meta Pixel ne sont pas actifs. Cette section sera mise à jour lorsque ces outils seront mis en service, avec leur fournisseur, leur durée et leur finalité exacts. Aucun chargement n'aura lieu sans votre consentement préalable.
      </p>

      <h3>3.4 Contenus externes embarqués</h3>
      <ul>
        <li><strong>Système de réservation Pure Informatique</strong> (book.pure-informatique.com) : utilisé pour la prise de rendez-vous. Ouvert dans une nouvelle fenêtre depuis les boutons « Réserver ».</li>
        <li><strong>Liens sociaux</strong> (Instagram, Facebook, LinkedIn) : aucun cookie n'est posé tant que vous ne cliquez pas sur le lien.</li>
        <li><strong>YouTube / Vimeo</strong> : non intégrés directement à ce jour. Toute future intégration sera bloquée tant que vous n'avez pas accepté la catégorie marketing.</li>
      </ul>

      <h2>4. Modifier ou retirer mon consentement</h2>
      <p>Vous pouvez modifier ou retirer votre consentement à tout moment :</p>
      <ul>
        <li>Via le lien « Gérer mes cookies » dans le pied de page de chaque page.</li>
        <li>En cliquant directement sur le bouton ci-dessous.</li>
      </ul>
      <button
        type="button"
        onClick={openPreferences}
        className="not-prose inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-bold text-white bg-gradient-to-r from-pink-500 to-fuchsia-500 hover:opacity-90 transition-opacity"
        data-testid="cookie-policy-manage-btn"
      >
        Gérer mes préférences cookies
      </button>

      <h2>5. Mise à jour de cette politique</h2>
      <p>En cas de changement important (nouveau fournisseur, nouvelle finalité, nouveaux cookies), une nouvelle demande de consentement vous sera présentée et la version de la politique sera incrémentée.</p>

      <h2>6. Contact</h2>
      <p>Pour toute question sur les cookies : <a href="mailto:info@body-up.ch">info@body-up.ch</a>.</p>
    </LegalPageLayout>
  );
};

export default CookiePolicyFR;
